import React from 'react';
import { createPortal } from 'react-dom';
import { IconShoppingCart } from '@tabler/icons-react';
import { useTranslate } from '@src/core/hooks';
import type { UnitPrice } from '@src/domains/products/features/product-booking/core/computeUnitPrice';
import { useProductBookingSummary } from './useProductBookingSummary';
import { BarRoot, CtaButton, PriceInfo, PriceLabel, PriceRow, PriceValue, ViewSummaryLink } from './styles';

type Props = {
  cardRef: React.RefObject<HTMLElement | null>;
  unitPrice: UnitPrice | null;
};

const ProductBookingSummary: React.FC<Props> = ({ cardRef, unitPrice }) => {
  const { i18n } = useTranslate();
  const {
    shouldRender,
    visible,
    stacked,
    showTotal,
    priceLabel,
    priceValue,
    ctaLabel,
    actionColor,
    handleViewSummary,
  } = useProductBookingSummary({ cardRef, unitPrice });

  if (!shouldRender) return null;

  const bar = (
    <BarRoot
      visible={visible}
      data-testid='product-adder-summary'
      aria-hidden={!visible}
      data-view={showTotal ? 'configured' : 'unit-price'}
    >
      {showTotal && (
        <ViewSummaryLink type='button' onClick={handleViewSummary}>
          {i18n('product_booking_view_summary')}
        </ViewSummaryLink>
      )}
      <PriceRow stacked={stacked}>
        <PriceInfo>
          <PriceLabel>{priceLabel}</PriceLabel>
          <PriceValue>{priceValue}</PriceValue>
        </PriceInfo>
        <CtaButton
          actionColor={actionColor}
          variant='contained'
          fullWidth={stacked}
          startSlot={<IconShoppingCart />}
          onClick={handleViewSummary}
        >
          {ctaLabel}
        </CtaButton>
      </PriceRow>
    </BarRoot>
  );

  return createPortal(bar, document.body);
};

export default ProductBookingSummary;
